/**
 * Maintenance Page
 * Shown to public visitors when maintenance mode is enabled
 */

import { Settings, Mail, Clock } from 'lucide-react';
import { SEO } from '../../components/common/SEO';
import { useWebsiteContentOptional } from '../../contexts/WebsiteContentContext';
import { useLanguage } from '../../contexts/LanguageContext';
import EditableElement from '../../components/admin/EditableElement';

export function Maintenance({ message }: { message?: string }) {
  const content = useWebsiteContentOptional();
  const { t } = useLanguage();

  // Helper to get content with fallback (CMS > i18n > hardcoded)
  const get = (key: string, fallback: string, i18nKey?: string) => {
    const translatedFallback = i18nKey ? t(i18nKey, fallback) : fallback;
    return content?.getContent('maintenance', key, translatedFallback) ?? translatedFallback;
  };

  return (
    <>
      <SEO page="maintenance" />
      <div className="min-h-screen bg-gradient-to-br from-primary-600 to-primary-800 flex items-center justify-center p-4 relative overflow-hidden">
        {/* Background pattern - matches hero */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <pattern id="maintenance-pattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
              <circle cx="20" cy="20" r="2" fill="currentColor" />
            </pattern>
            <rect width="100%" height="100%" fill="url(#maintenance-pattern)" />
          </svg>
        </div>

        <div className="relative max-w-lg w-full text-center">
          {/* Logo */}
          <div className="mb-10">
            <img
              src="/logo-light.svg"
              alt="RUMIO"
              className="h-12 mx-auto"
            />
          </div>

          {/* Animated gear icon */}
          <div className="mx-auto w-24 h-24 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center mb-8 border border-white/20">
            <Settings className="w-12 h-12 text-accent animate-spin" style={{ animationDuration: '8s' }} />
          </div>

          {/* Message */}
          <EditableElement section="maintenance" field="title">
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {get('title', "We're Making Things Better", 'maintenance.title')}
            </h1>
          </EditableElement>
          <EditableElement section="maintenance" field="subtitle">
            <p className="text-primary-100 text-lg mb-8 leading-relaxed max-w-md mx-auto">
              {message || get('subtitle', "We are currently performing maintenance. Please check back soon!", 'maintenance.subtitle')}
            </p>
          </EditableElement>

          {/* ETA card */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-white/20">
            <div className="flex items-center justify-center gap-3 text-white mb-3">
              <Clock className="w-5 h-5 text-accent" />
              <EditableElement section="maintenance" field="expected_downtime_label">
                <span className="font-medium">{get('expected_downtime_label', 'Expected downtime', 'maintenance.expectedDowntimeLabel')}</span>
              </EditableElement>
            </div>
            <EditableElement section="maintenance" field="expected_downtime">
              <p className="text-2xl font-bold text-accent">{get('expected_downtime', 'Under 30 minutes', 'maintenance.expectedDowntime')}</p>
            </EditableElement>
          </div>

          {/* Contact */}
          <div className="mb-8">
            <EditableElement section="maintenance" field="contact_label">
              <p className="text-primary-200 text-sm mb-3">{get('contact_label', 'Questions? Reach out to us:', 'maintenance.contactLabel')}</p>
            </EditableElement>
            <a
              href="mailto:support@rumio.world"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-primary-600 font-semibold rounded-lg hover:bg-primary-50 transition-colors"
            >
              <Mail className="w-4 h-4" />
              {t('maintenance.supportEmail', 'support@rumio.world')}
            </a>
          </div>

          {/* Admin Login Link */}
          <div className="text-sm text-primary-300">
            <a
              href="/login"
              className="hover:text-white underline underline-offset-2 transition-colors"
            >
              {get('admin_login', 'Admin Login', 'maintenance.adminLogin')}
            </a>
          </div>
        </div>
      </div>
    </>
  );
}

export default Maintenance;
